import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Divider,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import AddIcon from '@material-ui/icons/Add'

import { axiosInstance, getInitials } from '../../../../helpers'

export type VendorSearchResult = {
    id: number
    name: string
    email?: string | null
    active_apartment_count?: number
}

interface Props {
    theme: Theme
    onSelectVendor: (vendor: VendorSearchResult) => void
    onCreateNew: (searchName: string, searchEmail: string) => void
    onSearchEzid: (id: number) => void
}

const isValidEzid = (value: string) =>
    /^\d+$/.test(value.trim()) && Number(value.trim()) > 0

export const StepFindVendor = (props: Props) => {
    const { theme, onSelectVendor, onCreateNew, onSearchEzid } = props

    const [searchName, setSearchName] = useState('')
    const [searchEmail, setSearchEmail] = useState('')
    const [searchEzid, setSearchEzid] = useState('')

    const [results, setResults] = useState<VendorSearchResult[]>([])
    const [loading, setLoading] = useState(false)
    const [searched, setSearched] = useState(false)
    const [ezidError, setEzidError] = useState('')

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const performSearch = useCallback((name: string, email: string) => {
        if (!name && !email) {
            setResults([])
            setSearched(false)
            return
        }
        setLoading(true)

        axiosInstance
            .post('company/vendor/search_vendors/', { name, email })
            .then((res) => {
                setResults(res.data.vendors ?? [])
                setSearched(true)
            })
            .catch(() => {
                setResults([])
                setSearched(true)
            })
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => {
        const name = searchName.trim()
        const email = searchEmail.trim().toLocaleLowerCase()

        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }

        if (!name && !email) {
            setResults([])
            setSearched(false)
            return
        }

        debounceRef.current = setTimeout(() => {
            performSearch(name, email)
        }, 300)

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
        }
    }, [searchName, searchEmail, performSearch])

    const handleEzidSearch = () => {
        const val = searchEzid.trim()
        if (!isValidEzid(val)) {
            setEzidError('Enter a valid numeric EZID')
            return
        }
        setEzidError('')
        onSearchEzid(Number(val))
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            style={{
                width: 640,
                height: '70vh',
                maxHeight: 550,
                minHeight: 400,
                padding: theme.spacing(3),
            }}
        >
            {/* Title */}
            <Typography
                variant="h6"
                style={{
                    fontWeight: 600,
                    marginBottom: theme.spacing(3),
                    color: theme.palette.text.primary,
                }}
            >
                Find a Vendor
            </Typography>

            {/* --- EZID exact lookup --- */}
            <Typography
                variant="subtitle2"
                style={{
                    fontWeight: 600,
                    color: theme.palette.grey[700],
                    marginBottom: theme.spacing(0.5),
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    letterSpacing: '0.05em',
                }}
            >
                Search by EZID
            </Typography>
            <Typography
                variant="caption"
                style={{
                    color: theme.palette.grey[500],
                    marginBottom: theme.spacing(1),
                    lineHeight: 1.4,
                }}
            >
                If your vendor has used EZOS before, ask them for their EZID for
                a quick lookup.
            </Typography>
            <Box
                display="flex"
                alignItems="flex-start"
                style={{ gap: theme.spacing(1) }}
            >
                <TextField
                    value={searchEzid}
                    onChange={(e) => {
                        setSearchEzid(e.target.value)
                        setEzidError('')
                    }}
                    placeholder="Enter vendor EZID"
                    size="small"
                    variant="outlined"
                    style={{ flex: 1 }}
                    error={!!ezidError}
                    helperText={ezidError}
                    onKeyDown={(e) => e.key === 'Enter' && handleEzidSearch()}
                />
                <Button
                    variant="contained"
                    style={{
                        textTransform: 'none',
                        backgroundColor: theme.palette.primary.dark,
                        color: '#FFF',
                        minWidth: 44,
                        height: 40,
                    }}
                    onClick={handleEzidSearch}
                >
                    <SearchIcon />
                </Button>
            </Box>

            {/* --- Divider --- */}
            <Box
                display="flex"
                alignItems="center"
                style={{ margin: theme.spacing(3, 0) }}
            >
                <Divider style={{ flex: 1 }} />
                <Typography
                    variant="body2"
                    style={{
                        margin: theme.spacing(0, 2),
                        color: theme.palette.grey[400],
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.05em',
                    }}
                >
                    or search by name / email
                </Typography>
                <Divider style={{ flex: 1 }} />
            </Box>

            {/* --- Name / Email fuzzy search --- */}
            <Box
                display="flex"
                alignItems="center"
                style={{ gap: theme.spacing(1) }}
            >
                <TextField
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    placeholder="Vendor Name"
                    size="small"
                    variant="outlined"
                    style={{ flex: 1 }}
                />
                <TextField
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    placeholder="Email"
                    size="small"
                    variant="outlined"
                    style={{ flex: 1 }}
                />
                {loading && <CircularProgress size={20} />}
            </Box>

            {/* Results list */}
            <Box
                style={{
                    marginTop: theme.spacing(1.5),
                    flex: 1,
                    overflowY: 'auto',
                    border: searched
                        ? `1px solid ${theme.palette.divider}`
                        : 'none',
                    borderRadius: theme.shape.borderRadius,
                    minHeight: 0,
                }}
            >
                {searched && results.length === 0 && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        style={{ padding: theme.spacing(3) }}
                    >
                        <Typography
                            variant="body2"
                            style={{
                                color: theme.palette.grey[500],
                                marginBottom: theme.spacing(1.5),
                            }}
                        >
                            No vendors found
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            style={{
                                textTransform: 'none',
                                color: theme.palette.primary.dark,
                                borderColor: theme.palette.primary.dark,
                                fontWeight: 600,
                            }}
                            onClick={() =>
                                onCreateNew(
                                    searchName.trim(),
                                    searchEmail.trim().toLocaleLowerCase(),
                                )
                            }
                        >
                            Create New Vendor
                        </Button>
                    </Box>
                )}
                {searched && results.length > 0 && (
                    <>
                        {results.map((vendor) => (
                            <Box
                                key={vendor.id}
                                display="flex"
                                alignItems="center"
                                style={{
                                    padding: theme.spacing(1.5, 2),
                                    cursor: 'pointer',
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    transition: 'background-color 0.15s',
                                }}
                                onClick={() => onSelectVendor(vendor)}
                                onMouseEnter={(e) => {
                                    const el = e.currentTarget as HTMLElement
                                    el.style.backgroundColor =
                                        theme.palette.action.hover
                                }}
                                onMouseLeave={(e) => {
                                    const el = e.currentTarget as HTMLElement
                                    el.style.backgroundColor = 'transparent'
                                }}
                            >
                                <Avatar
                                    style={{
                                        backgroundColor:
                                            theme.palette.primary.dark,
                                        width: 36,
                                        height: 36,
                                        fontSize: 14,
                                        marginRight: theme.spacing(1.5),
                                    }}
                                >
                                    {getInitials(vendor.name)}
                                </Avatar>
                                <Box flex={1} style={{ minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            fontWeight: 600,
                                            lineHeight: 1.3,
                                        }}
                                        noWrap
                                    >
                                        {vendor.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        style={{
                                            color: theme.palette.grey[600],
                                        }}
                                        noWrap
                                    >
                                        {vendor.email ?? ''}
                                    </Typography>
                                </Box>
                                {vendor.active_apartment_count != null && (
                                    <Box
                                        style={{
                                            backgroundColor:
                                                theme.palette.grey[100],
                                            borderRadius: 4,
                                            padding: theme.spacing(0.25, 1),
                                            marginLeft: theme.spacing(1),
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            style={{
                                                color: theme.palette.grey[600],
                                                fontWeight: 500,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {vendor.active_apartment_count}{' '}
                                            {vendor.active_apartment_count === 1
                                                ? 'property'
                                                : 'properties'}
                                        </Typography>
                                    </Box>
                                )}
                                <Box
                                    style={{
                                        backgroundColor:
                                            theme.palette.grey[100],
                                        borderRadius: 4,
                                        padding: theme.spacing(0.25, 1),
                                        marginLeft: theme.spacing(1),
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        style={{
                                            color: theme.palette.grey[600],
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        EZID {vendor.id}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                        <Box
                            display="flex"
                            justifyContent="center"
                            style={{ padding: theme.spacing(1.5) }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                style={{
                                    textTransform: 'none',
                                    color: theme.palette.primary.dark,
                                    borderColor: theme.palette.primary.dark,
                                    fontWeight: 600,
                                }}
                                onClick={() =>
                                    onCreateNew(
                                        searchName.trim(),
                                        searchEmail.trim().toLocaleLowerCase(),
                                    )
                                }
                            >
                                Add New Vendor
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    )
}
